import Grid from "@material-ui/core/Grid";
import useSWR from "swr";
import useTranslation from "next-translate/useTranslation";
import React, { FC } from "react";
import { PriceType } from "types";
import { TheCycleverseTheme } from "src/theme";
import { makeStyles } from "@material-ui/core/styles";

import ButtonLink from "../../Navigation/ButtonLink";
import PriceTag from "./PriceTag";
import Text from "../Text/Text";
import { default as bestProducts } from "../../../bestProducts.json";
import { queryProducts } from "../../../graphql/queries";
import { groupBy } from "src/components/common/ArrayFunctions";

const useStyles = makeStyles((theme: TheCycleverseTheme) => ({
  productBestDetail: {
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(6),
    textAlign: "center",
    backgroundColor: theme.palette.background.secondary,
  },
  headline2: {
    fontSize: 36,
    marginTop: 12,
    fontFamily: theme.highlightFontFamily,
    fontWeight: 900,
    maxWidth: 900,
    display: "inline-block",
    lineHeight: "1.1",
  },
  content: {
    textAlign: "left",
    maxWidth: 500,
    width: "100%",
    display: "inline-block",
    fontSize: "medium",
  },
  intro: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(6),
  },
  productCard: {
    borderBottom: `1px solid ${theme.unflashyColor}`,
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  productImage: {
    // paddingTop: '100%',
    minHeight: 140,
    marginRight: 5,
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
    // [theme.breakpoints.up("sm")]: {
    //     minHeight: 200,
    // }
  },
  brand: {
    color: theme.hightlightColor,
  },
  title: {
    fontSize: "medium",
    fontWeight: 300,
    marginBlockStart: "unset",
    marginBlockEnd: "unset",
    marginInlineStart: "unset",
    marginInlineEnd: "unset",
  },
  openButton: {
    backgroundColor: theme.openButtonColor,
    color: theme.palette.primary.main,
    marginTop: theme.spacing(2),
  },
  more: {
    textAlign: "center",
    marginBottom: theme.spacing(5),
  },
}));

const getMinPrice = (category, type, maxPrice) => {
  const bikes = bestProducts.items["fahrraeder"];
  const bike = bikes.find((bike) => bike.key == category);
  if (!bike) {
    return null;
  }
  const priceSegment = bike.priceSegments;

  if (type == "highEnd") {
    return priceSegment[priceSegment.length - 1];
  }

  const lowerPrices = priceSegment.filter((price) => price < maxPrice);
  if (lowerPrices.length <= 0) {
    return 0;
  }
  return lowerPrices[lowerPrices.length - 1];
};

const ProductBestDetail: FC<{
  categoryPath: string;
  maxPrice?: number;
  type: PriceType;
  name: string;
  category: string;
  translationsNamespace: string;
}> = ({ categoryPath, maxPrice, type, name, category, translationsNamespace }) => {
  const classes = useStyles();
  const { t, lang } = useTranslation(translationsNamespace);
  const displayCount = 5;
  const minPrice = getMinPrice(category, type, maxPrice);

  const { data } = useSWR([
    queryProducts,
    JSON.stringify({
      action: "search",
      category: categoryPath,
      visibility: "public",
      priceMin: minPrice ? minPrice : 0,
      priceMax: maxPrice ? maxPrice : 9999999,
      sort: "priceDesc",
      size: displayCount * 20,
    }),
  ]);

  const title =
    type === "maxPrice"
      ? t("bestBikesCommons:productBestBikeDetailHeadlinePrice", { name, maxPrice })
      : t("bestBikesCommons:productBestBikeDetailHeadlineHighEnd", { name });

  if (!data) {
    return null;
  }

  const products = data.queryProducts?.items;

  if (!products || products.length == 0) {
    return null; // TODO: show some text that no products were found
  }

  const productsGroupedByBrand = groupBy(products, "brand");
  let priceOffset = products[0].price.amount;
  const productsFromAllBrands = Object.keys(productsGroupedByBrand).map((key) => {
    const list = productsGroupedByBrand[key];
    return list[Math.floor(list.length / 2)];
  });

  const displayedProducts = productsFromAllBrands.filter((elt, idx, arr) => {
    const howMany = displayCount;
    if (arr.length < howMany) {
      return true;
    }
    const nth = Math.floor(arr.length / howMany);
    if (nth <= 1) {
      return idx < howMany;
    }
    return idx % nth === 0;
  });

  return (
    <div className={classes.productBestDetail} data-test="best-segment-page">
      <h1 className={classes.headline2}>{title}</h1>
      <div>
        <div className={classes.content} data-test={`page-beste-${category}-unter-${maxPrice}`}>
          <div className={classes.intro}>{t("productBestBikeDetailIntro")}</div>

          {displayedProducts.map((product) => {
            const imageSrc = product.renditionMedium ? product.renditionMedium : product.images[0].uri;
            return (
              <Grid key={product.id} container className={classes.productCard}>
                <Grid xs={5} item>
                  <div className={classes.productImage} style={{ backgroundImage: `url('${imageSrc}')` }} />
                </Grid>
                <Grid xs={7} item>
                  <PriceTag
                    price={product.price}
                    // className={classes.price}
                  />
                  <div className={classes.brand}>{product.brand}</div>
                  <h2 className={classes.title}>{product.title}</h2>
                  <ButtonLink
                    href="/[...generic]"
                    as={`/${category}/${product.seoId}`}
                    target="new"
                    buttonClassName={classes.openButton}
                  >
                    {t("bestBikesCommons:productBestBikeDetailOpen")}
                  </ButtonLink>
                </Grid>
              </Grid>
            );
          })}

          <div className={classes.more}>
            <ButtonLink href="/[...generic]" as={`/${category}`} buttonClassName={classes.openButton}>
              {t("bestBikesCommons:productBestBikeDetailMehr")}
            </ButtonLink>
          </div>

          <Text anchorId="info" useCache={true} id={`product-best-detail-${lang}-${category}`} />
        </div>
      </div>
    </div>
  );
};

export default ProductBestDetail;
